import { useEffect, type MutableRefObject, type RefObject } from 'react'
import type { Block } from '../../lib/types'
import { newBlockId } from '../../lib/blocks'
import { pastedParts, type PastedPart } from '../../lib/paste'
import { htmlBeside } from '../../components/editor/caret'
import { pasteAcross } from './blockOps'

const EDITABLE = '[data-block][contenteditable="true"]'
/** Marks the paragraphs whose editing is off while a selection runs across them. */
const OFF = 'data-range-off'

const elementOf = (n: Node) => (n.nodeType === Node.ELEMENT_NODE ? (n as Element) : n.parentElement)

/** The column row (BlockList's `data-row`) a node is in. */
function rowOf(page: HTMLElement, n: Node): HTMLElement | null {
  const row = elementOf(n)?.closest<HTMLElement>('[data-row]') ?? null
  return row && page.contains(row) ? row : null
}

/** The row's own paragraph, heading or pull quote, if the node is in it (not in a card, a margin note…). */
function editableOf(row: HTMLElement, n: Node): HTMLElement | null {
  const el = elementOf(n)?.closest<HTMLElement>('[data-block]') ?? null
  return el && el.dataset.block === row.dataset.row ? el : null
}

function textLength(html: string): number {
  const box = document.createElement('div')
  box.innerHTML = html
  return (box.textContent ?? '').length
}

/**
 * Selecting across paragraphs while writing (Andrew, 9/30). Each paragraph is
 * its own editable, and a browser keeps a drag selection inside the editable
 * it started in, so it used to stop at the end of the paragraph. When a drag
 * leaves its paragraph, editing is switched off on the page's paragraphs for
 * as long as the selection stands, and it runs on across them like any text
 * on a page. Then:
 * - copying works as usual;
 * - Backspace, Delete, Enter or cut take the selection out and join what's
 *   left of the first and last paragraphs (blockOps.deleteAcross);
 * - typing a character or pasting replaces it (a paste of several
 *   paragraphs or headings brings them in as blocks, as in a paragraph);
 * - a click, Escape or an arrow key goes back to editing.
 */
export function useCrossBlockSelection({
  pageRef,
  docRef,
  blockOp,
  bumpRev,
  focusLater,
  disabled,
}: {
  pageRef: RefObject<HTMLElement | null>
  docRef: MutableRefObject<{ blocks: Block[] }>
  /** An undoable block operation: what the browser's own undo can't take back. */
  blockOp: (fn: (bs: Block[]) => Block[]) => void
  bumpRev: (id: string) => void
  focusLater: (id: string, where: 'start' | 'end' | number) => void
  disabled: boolean
}): void {
  useEffect(() => {
    const page = pageRef.current
    if (!page || disabled) return
    /** The paragraph a drag started in. */
    let drag: HTMLElement | null = null
    let active = false

    const enter = () => {
      active = true
      for (const el of Array.from(page.querySelectorAll<HTMLElement>(EDITABLE))) {
        el.setAttribute('contenteditable', 'false')
        el.setAttribute(OFF, '')
      }
    }
    const exit = () => {
      if (!active) return
      active = false
      for (const el of Array.from(page.querySelectorAll<HTMLElement>(`[${OFF}]`))) {
        el.setAttribute('contenteditable', 'true')
        el.removeAttribute(OFF)
      }
    }

    /** Back to editing, keeping the selection (collapsed to one end, or as it is when it's inside one paragraph). */
    const exitKeeping = (collapse: 'start' | 'end' | null) => {
      const sel = window.getSelection()
      const r = sel?.rangeCount ? sel.getRangeAt(0).cloneRange() : null
      exit()
      if (!sel || !r) return
      if (collapse) r.collapse(collapse === 'start')
      const el = elementOf(collapse === 'end' ? r.endContainer : r.startContainer)?.closest<HTMLElement>('[data-block]')
      if (!el || !page.contains(el)) {
        sel.removeAllRanges()
        return
      }
      el.focus({ preventScroll: true })
      sel.removeAllRanges()
      sel.addRange(r)
    }

    /** Does the selection run across column rows? */
    const spansRows = () => {
      const sel = window.getSelection()
      if (!sel || sel.isCollapsed || !sel.rangeCount) return false
      const r = sel.getRangeAt(0)
      const a = rowOf(page, r.startContainer)
      const z = rowOf(page, r.endContainer)
      return !!a && !!z && a !== z
    }

    /** Take the selection out, putting what was pasted (or typed) where it was. */
    const replace = (parts: PastedPart[]) => {
      const sel = window.getSelection()
      const r = sel?.rangeCount ? sel.getRangeAt(0) : null
      const a = r && rowOf(page, r.startContainer)
      const z = r && rowOf(page, r.endContainer)
      if (!r || !a || !z) {
        // It runs into the title or the byline: nothing to join there.
        exitKeeping('end')
        return
      }
      const aEl = editableOf(a, r.startContainer)
      const zEl = editableOf(z, r.endContainer)
      const head = aEl ? htmlBeside(aEl, 'before', r.startContainer, r.startOffset) : null
      const tail = zEl ? htmlBeside(zEl, 'after', r.endContainer, r.endOffset) : null
      exit()
      sel!.removeAllRanges()
      const res = pasteAcross(docRef.current.blocks, a.dataset.row!, z.dataset.row!, head, tail, parts, newBlockId)
      blockOp(() => res.blocks)
      bumpRev(res.keptId)
      focusLater(res.focus.id, (res.focus.afterHead && head !== null ? textLength(head) : 0) + res.focus.at)
    }
    const typed = (text: string): PastedPart[] => (text ? [{ kind: 'text', text }] : [])

    const onPointerDown = (e: PointerEvent) => {
      // A click goes back to editing first, so the browser can put the caret where it lands.
      if (active) exit()
      drag = null
      if (e.button !== 0 || e.pointerType === 'touch') return
      const el = (e.target as Element | null)?.closest?.<HTMLElement>(EDITABLE)
      if (el && page.contains(el)) drag = el
    }
    const onPointerMove = (e: PointerEvent) => {
      if (!drag || active || !(e.buttons & 1)) return
      const box = drag.getBoundingClientRect()
      if (e.clientY < box.top - 4 || e.clientY > box.bottom + 4) enter()
    }
    const onPointerUp = () => {
      drag = null
      // Dragged out and back into the paragraph it started in: an ordinary selection there.
      if (active && !spansRows()) exitKeeping(null)
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (!active) return
      const k = e.key
      if (k === 'Backspace' || k === 'Delete' || k === 'Enter') {
        e.preventDefault()
        replace([])
      } else if (k === 'Escape') {
        e.preventDefault()
        exitKeeping('end')
      } else if (!e.shiftKey && (k === 'ArrowLeft' || k === 'ArrowUp')) {
        e.preventDefault()
        exitKeeping('start')
      } else if (!e.shiftKey && (k === 'ArrowRight' || k === 'ArrowDown')) {
        e.preventDefault()
        exitKeeping('end')
      } else if (k === 'Tab') {
        exit()
      } else if (k.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
        e.preventDefault()
        replace(typed(k))
      }
    }
    // Cut and paste are caught on the way down and go no further: the browser
    // sends them to the paragraph the selection starts in, whose own paste
    // (useBlockEditor) would edit a paragraph that isn't editable now, across
    // React's rows: the crash Niki hit pasting over a selection (10/6).
    const onCut = (e: ClipboardEvent) => {
      if (!active) return
      e.preventDefault()
      e.stopPropagation()
      e.clipboardData?.setData('text/plain', window.getSelection()?.toString() ?? '')
      replace([])
    }
    const onPaste = (e: ClipboardEvent) => {
      if (!active) return
      e.preventDefault()
      e.stopPropagation()
      replace(pastedParts(e.clipboardData?.getData('text/html') ?? '', e.clipboardData?.getData('text/plain') ?? ''))
    }

    document.addEventListener('pointerdown', onPointerDown, true)
    document.addEventListener('pointermove', onPointerMove)
    document.addEventListener('pointerup', onPointerUp)
    document.addEventListener('keydown', onKeyDown, true)
    document.addEventListener('cut', onCut, true)
    document.addEventListener('paste', onPaste, true)
    return () => {
      exit()
      document.removeEventListener('pointerdown', onPointerDown, true)
      document.removeEventListener('pointermove', onPointerMove)
      document.removeEventListener('pointerup', onPointerUp)
      document.removeEventListener('keydown', onKeyDown, true)
      document.removeEventListener('cut', onCut, true)
      document.removeEventListener('paste', onPaste, true)
    }
  }, [pageRef, docRef, blockOp, bumpRev, focusLater, disabled])
}
